
import { connect } from 'react-redux';
import { useEffect, useRef } from 'react'

import { eventLog } from './eventHelper';

// Algolia.
import { ALGOLIA_API_KEY, ALGOLIA_APP_ID } from '../constants/algolia_constants';
import algoliasearch from 'algoliasearch/lite';
import 'instantsearch.css/themes/satellite.css';
import {
    useHits,
    useSearchBox,
} from 'react-instantsearch-core';

import {
    Configure,
    Index,
    InstantSearch
} from 'react-instantsearch';

import { SearchRounded } from '@mui/icons-material';
import Highlighter from 'react-highlight-words';

// Components.

import HighlightSearchResults from './HighlightSearchResults';
import EpisodeSearchResults from './EpisodeSearchResults';
import ApplePodcastsSearch from './ApplePodcastsSearch';
import SpeakerRows from './SpeakerRows';
import SearchEmptyState from './SearchEmptyState';
import SearchResultsLoading from './SearchResultsLoading';
import SegmentedTabs, { SegmentedTabPanels } from './SegmentedTabs';
import useSearchResultsSelection from './useSearchResultsSelection';
import { speakersFromSearchHits } from '../utils/speakerSearch';

const searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY);
const SEARCH_RESULTS_TAB_OPTIONS = [
    { value: 'podcasts', label: 'Shows' },
    { value: 'speakers', label: 'Speakers' },
    { value: 'highlights', label: 'Highlights' },
    { value: 'episodes', label: 'Episodes' },
];

const GlobalHighlightSearchResults = ({ onSearchEpisodes }) => {
    const { query } = useSearchBox();

    useEffect(() => {
        document.getElementById('audio')?.pause();
    }, [query]);

    return <HighlightSearchResults query={query} onSearchEpisodes={onSearchEpisodes} />;
};

const GlobalEpisodeSearchResults = () => {
    const { query } = useSearchBox();
    return <EpisodeSearchResults query={query} includesVerticalCardPadding={false} />;
};

const GlobalSearchTabPanels = (props) => {
    const { query } = useSearchBox();
    // A new query searches only the selected category, including when a
    // suggested topic refines it without navigating to a different route.
    return <SegmentedTabPanels key={query} {...props} scrollResetKey={query} />;
};

const SearchTopicSuggestions = (props) => {
    const { query, refine } = useSearchBox(props);
    return (
        <>
            <Configure hitsPerPage={3} />
            <TopicSuggestionHits query={query} refine={refine} />
        </>
    );
};

const TopicSuggestionHits = (props) => {
    const { hits, results } = useHits(props);

    const suggestionHits = hits.filter((hit) => hit.name !== props.query.toLowerCase());

    const searchHit = (hitName) => () => {
        eventLog(13, {
            'topicId': hitName,
        });
        props.refine(hitName);
        const inputElem = document.getElementsByClassName('aa-Input')[0];
        if (!!inputElem) {
            inputElem.value = hitName;
        } else {
            const inputElemMobile = document.getElementsByClassName('aa-DetachedSearchButtonQuery')[0];
            if (!!inputElemMobile) {
                inputElemMobile.innerText = hitName;
            }
        }
    }

    if (!results || results.__isArtificial || results.query !== props.query || suggestionHits.length === 0) {
        return null;
    }

    return (
        <div className='searchResults' style={{ padding: '10px 20px 0px' }}>
            <div className='searchHelperLabel' style={{ paddingBottom: '8px' }}>
                Suggested searches
            </div>
            <div className='globalSearchSuggestionList'>
                {suggestionHits.map((hit, index) => (
                    <div key={hit.name}>
                        <button
                            type='button'
                            onClick={searchHit(hit.name)}
                            className='globalSearchSuggestionRow'
                        >
                            <div className='globalSearchSuggestionIcon'>
                                <SearchRounded style={{ color: 'var(--app-text-secondary)', fontSize: '20px' }} />
                            </div>
                            <div className='globalSearchSuggestionText'>
                                <Highlighter
                                    highlightClassName="ais-Highlight-highlighted"
                                    unhighlightClassName="ais-Highlight-nonHighlighted"
                                    searchWords={[props.query]}
                                    autoEscape={true}
                                    textToHighlight={hit.name}
                                />
                            </div>
                        </button>
                        {index !== suggestionHits.length - 1 ?
                            <div className='globalSearchSuggestionDivider' />
                            :
                            null}
                    </div>
                ))}
            </div>
        </div>
    );
};

const SearchPodcasts = (props) => {
    const { query } = useSearchBox(props);

    return (
        <ApplePodcastsSearch query={query} emptyMessage={`No shows matching "${query}".`} />
    );
};

const SearchSpeakers = (props) => {
    const { query } = useSearchBox(props);
    return (
        <Index indexName="speakers">
            <Configure hitsPerPage={5} />
            <SpeakerHits query={query} />
        </Index>
    );
};

const SpeakerHits = (props) => {
    const { hits, results } = useHits(props);

    // Algolia matches both names and descriptions, including typos. Keep its
    // ranked matches instead of requiring the query to occur in the name.
    const speakers = speakersFromSearchHits(hits);

    if (!results || results.__isArtificial || results.query !== props.query) {
        return <SearchResultsLoading kind='speakers' />;
    }

    return (
        speakers.length > 0 ? (
            <div style={{ padding: '10px 20px' }}>
                <SpeakerRows
                    speakers={speakers}
                    rowGap='8px'
                />
            </div>
        ) : <SearchEmptyState message={`No speakers matching "${props.query}".`} />
    )
};

const AlgoliaSearch = ({
    initialQuery,
    initialTab = 'podcasts',
    noQueryView,
    searchBox,
}) => {
    const initialSearchQuery = (initialQuery || '').trim();
    const scrollTargetRef = useRef(null);
    const selection = useSearchResultsSelection('global', initialTab, SEARCH_RESULTS_TAB_OPTIONS);
    const title = initialSearchQuery
        ? SEARCH_RESULTS_TAB_OPTIONS.find(option => option.value === selection.selectedValue).label
        : 'Search';

    useEffect(() => {
        if (!!initialQuery) {
            eventLog(13, {
                'topicId': initialQuery,
            });
        }
    }, [initialQuery]);

    return (
        <div ref={scrollTargetRef}>
            <div className='algoliaCustom appSearchBoxDock fixToTop globalSearchDock'>
                {searchBox}
                <div className='globalSearchControls'>
                    <SegmentedTabs
                        {...selection}
                        options={SEARCH_RESULTS_TAB_OPTIONS}
                        fontSize='12px'
                    />
                </div>
            </div>
            <div className='globalSearchContent'>
                <h1 className='largeHeading globalSearchTitle'>{title}</h1>
                {!initialSearchQuery ? noQueryView : <InstantSearch
                    key={initialSearchQuery}
                    indexName="topics"
                    // Replacing another query widget must not clear the shared
                    // query and spuriously reset the reader's scroll position.
                    future={{ preserveSharedStateOnUnmount: true }}
                    initialUiState={{
                        "topics": {
                            query: initialSearchQuery,
                        }
                    }}
                    searchClient={searchClient}>
                    <GlobalSearchTabPanels
                        {...selection}
                        scrollTargetRef={scrollTargetRef}
                        options={SEARCH_RESULTS_TAB_OPTIONS}
                        panels={{
                            highlights: <GlobalHighlightSearchResults onSearchEpisodes={() => selection.onChange('episodes')} />,
                            speakers: <SearchSpeakers />,
                            podcasts: <SearchPodcasts />,
                            episodes: <GlobalEpisodeSearchResults />,
                        }}
                        hideControls
                        keepMounted
                        lazyMount
                    />
                    <SearchTopicSuggestions />
                </InstantSearch>}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({

})

const mapActionsToProps = {

}

export default connect(mapStateToProps, mapActionsToProps)(AlgoliaSearch);
